"use client";

import { useEffect, useState } from "react";
import { useSession } from "next-auth/react";
import { useParams, useRouter } from "next/navigation";
import { toast } from "sonner";
import AppButton from "@/components/app-button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import PasswordInput from "@/features/auth/components/password-input";
import { Label } from "@/components/ui/label";

interface ChangePasswordDialogProps {
  isOpen: boolean;
  setIsOpen: (open: boolean) => void;
}

export default function ChangePasswordDialog({
  isOpen,
  setIsOpen,
}: ChangePasswordDialogProps) {
  const router = useRouter();
  const { locale } = useParams<{ locale: string }>();
  const { data: session } = useSession();
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (isOpen) {
      return;
    }

    setCurrentPassword("");
    setNewPassword("");
    setConfirmPassword("");
    setIsSubmitting(false);
  }, [isOpen]);

  const handleSubmit = async () => {
    if (isSubmitting) return;
    if (!currentPassword.trim() || !newPassword.trim() || !confirmPassword.trim()) {
      toast.error("Vui lòng nhập đầy đủ thông tin");
      return;
    }

    if (newPassword !== confirmPassword) {
      toast.error("Xác nhận mật khẩu mới không khớp");
      return;
    }

    if ((session as any)?.accessToken == null) {
      toast.error("Thiếu access token");
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await fetch(
        `${process.env.NEXT_PUBLIC_API_ENDPOINT}/v1/auth/change-password`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${(session as any).accessToken}`,
          },
          body: JSON.stringify({ currentPassword, newPassword }),
        }
      );

      const payload = await response.json().catch(() => null);

      if (!response.ok) {
        throw new Error(payload?.message || "Đổi mật khẩu thất bại");
      }

      toast.success("Đổi mật khẩu thành công");
      setIsOpen(false);
      router.replace(`/${locale}`);
      router.refresh();
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : "Đổi mật khẩu thất bại"
      );
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>Đổi mật khẩu</DialogTitle>
          <DialogDescription>
            Cập nhật mật khẩu để tiếp tục sử dụng tài khoản quản trị.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-2">
          <div className="space-y-2">
            <Label htmlFor="currentPassword">Mật khẩu hiện tại</Label>
            <PasswordInput
              id="currentPassword"
              value={currentPassword}
              onChange={(event) => setCurrentPassword(event.target.value)}
              autoComplete="current-password"
              className="focus-ring-subtle"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="newPassword">Mật khẩu mới</Label>
            <PasswordInput
              id="newPassword"
              value={newPassword}
              onChange={(event) => setNewPassword(event.target.value)}
              autoComplete="new-password"
              className="focus-ring-subtle"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="confirmPassword">Xác nhận mật khẩu mới</Label>
            <PasswordInput
              id="confirmPassword"
              value={confirmPassword}
              onChange={(event) => setConfirmPassword(event.target.value)}
              autoComplete="new-password"
              className="focus-ring-subtle"
            />
          </div>
        </div>

        <DialogFooter>
          <AppButton
            variant="primary"
            onClick={handleSubmit}
            disabled={
              isSubmitting ||
              !currentPassword.trim() ||
              !newPassword.trim() ||
              !confirmPassword.trim()
            }
          >
            {isSubmitting ? "Đang cập nhật..." : "Cập nhật mật khẩu"}
          </AppButton>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
